import { defaultCalculateParameters } from '../../../index';
import {
  MultiTripTravel_CalculationResult,
  MultiTripTravel_ChildInput,
  MultiTripTravel_PartnerInput,
} from './types';
import moment from 'moment';

const cancellationInsuranceValues = [1000, 1500, 2500, 3500];

const getSafeData = (data: any, def: any): any => {
  return data || def;
};

export class TravelQueryProvider {
  public validateAddressVariables(
    postcode: string,
    houseNumber: number,
    houseNumberAddition?: string
  ): object {
    return {
      postalCode: postcode,
      houseNumber,
      houseNumberAddition,
    };
  }

  public validateMultiTripVariables(
    searchCriteria: object,
    selectedFilters: object,
    credentials: object,
    sessionId: string
  ): object {
    let calculateParameters = {
      ...defaultCalculateParameters,
      postalCode: searchCriteria['postcode'] || '',
      effectiveDate: new Date().toISOString().slice(0, 10),
      houseNumber: searchCriteria['housenumber'] || '',
      partnerBirthdates: [{}],
      childrenBirthdates: [{}],
      numberOfDaysCoverage: 0,
      coverageAreaCode: '',
      costsMedicalTreatmentAdditionallyInsured: false,
      injuryAdditionallyInsured: false,
      insuredAmountTravelCancellation: 1500,
      winterSportsAdditionallyInsured: false,
      underwaterSportsAdditionallyInsured: false,
      legalAidAdditionallyInsured: false,
      businessTravelAdditionallyInsured: false,
      alternativeTransportAdditionallyInsured: false,
      roadsideAssistanceAdditionallyInsured: false,
      repatriationCostsAdditionallyInsured: false,
      dangerousSportsAdditionallyInsured: false,
    };
    const paymentTermInMonths = getSafeData(
      Array.isArray(selectedFilters['showPriceMode']) &&
        selectedFilters['showPriceMode'].length > 0
        ? selectedFilters['showPriceMode'][0]
        : 'month',
      'month'
    );

    calculateParameters.paymentTermInMonths =
      paymentTermInMonths == 'month' ? 1 : 12;

    calculateParameters.birthDate = moment(
      searchCriteria['birthdate'],
      'DD-MM-YYYY'
    ).format('YYYY-MM-DD');

    calculateParameters.insuredFamilyComposition =
      searchCriteria['insuredFamilyComposition'];

    calculateParameters.partnerBirthdates = [];
    calculateParameters.childrenBirthdates = [];

    let partyPartners: MultiTripTravel_PartnerInput[] = [];
    if (
      calculateParameters.insuredFamilyComposition === 'VH' ||
      calculateParameters.insuredFamilyComposition === 'VI'
    ) {
      let partnerBirthdate = getSafeData(
        searchCriteria['partnerBirthdate'],
        ''
      );
      if (partnerBirthdate.length > 0) {
        partnerBirthdate = moment(partnerBirthdate, 'DD-MM-YYYY').format(
          'YYYY-MM-DD'
        );
        partyPartners.push({ birthDate: partnerBirthdate });
      }
    }
    calculateParameters.partnerBirthdates = partyPartners;

    let partyChildren: MultiTripTravel_ChildInput[] = [];
    if (
      calculateParameters.insuredFamilyComposition === 'VK' ||
      calculateParameters.insuredFamilyComposition === 'VI'
    ) {
      partyChildren = this.getChildBirthdates(searchCriteria);
    }
    calculateParameters.childrenBirthdates = partyChildren;

    calculateParameters.numberOfDaysCoverage = parseInt(
      searchCriteria['numberOfDaysCoverage']
    );

    calculateParameters.coverageAreaCode = searchCriteria['coverageAreaCode'];

    calculateParameters.costsMedicalTreatmentAdditionallyInsured =
      selectedFilters['medical'][0] !== '-1';

    calculateParameters.injuryAdditionallyInsured =
      selectedFilters['accidents'][0] !== '-1';

    calculateParameters.insuredAmountTravelCancellation =
      cancellationInsuranceValues[selectedFilters['cancellation'][0]] || 0;

    calculateParameters.winterSportsAdditionallyInsured =
      selectedFilters['wintersports'][0] !== '-1';

    calculateParameters.underwaterSportsAdditionallyInsured =
      selectedFilters['underwatersports'][0] !== '-1';

    calculateParameters.legalAidAdditionallyInsured =
      selectedFilters['travellegal'][0] !== '-1';

    calculateParameters.businessTravelAdditionallyInsured =
      selectedFilters['businesstrip'][0] !== '-1';

    calculateParameters.alternativeTransportAdditionallyInsured =
      selectedFilters['transportation'][0] !== '-1';

    calculateParameters.roadsideAssistanceAdditionallyInsured =
      selectedFilters['transportation'][0] !== '-1';

    calculateParameters.repatriationCostsAdditionallyInsured =
      selectedFilters['transportation'][0] !== '-1';

    calculateParameters.dangerousSportsAdditionallyInsured =
      selectedFilters['dangeroussports'][0] !== '-1';

    return {
      request: {
        policyPolicyDetails: [
          {
            paymentTermInMonths: calculateParameters.paymentTermInMonths,
            effectiveDate: calculateParameters.effectiveDate,
            partyPolicyHolders: [
              {
                birthDate: calculateParameters.birthDate,
                addresses: [
                  {
                    postalCode: calculateParameters.postalCode,
                    houseNumber: parseInt(calculateParameters.houseNumber),
                    houseNumberAddition: null,
                  },
                ],
                partyPartners: calculateParameters.partnerBirthdates,
                partyChildren: calculateParameters.childrenBirthdates,
              },
            ],
            coverageCancellations: [
              {
                petIllnessDeathAdditionallyInsured: false,
              },
            ],
            coverageTravels: [
              {
                carrierCoverageCode: 'V3008',
                numberOfDaysCoverage: calculateParameters.numberOfDaysCoverage,
                extendedStayAdditionallyInsured: false,
                alternativeTransportAdditionallyInsured:
                  calculateParameters.alternativeTransportAdditionallyInsured,
                repatriationCostsAdditionallyInsured:
                  calculateParameters.repatriationCostsAdditionallyInsured,
                injuryAdditionallyInsured:
                  calculateParameters.injuryAdditionallyInsured,
                businessTravelAdditionallyInsured:
                  calculateParameters.businessTravelAdditionallyInsured,
                costsMedicalTreatmentAdditionallyInsured:
                  calculateParameters.costsMedicalTreatmentAdditionallyInsured,
                dangerousSportsAdditionallyInsured:
                  calculateParameters.dangerousSportsAdditionallyInsured,
                underwaterSportsAdditionallyInsured:
                  calculateParameters.underwaterSportsAdditionallyInsured,
                extraAmountGolfEquipmentAdditionallyInsured: false,
                extraAmountDivingEquipmentAdditionallyInsured: false,
                returnCommissionPercentage: 0,
                moneyChequesAdditionallyInsured: false,
                coverageAreaCode: calculateParameters.coverageAreaCode,
                deductibleAmount: 0,
                insuredFamilyComposition:
                  calculateParameters.insuredFamilyComposition,
                exceptionalCostsAdditionallyInsured: true,
                assistanceExcludingRoadsideAdditionallyInsured: false,
                legalAidAdditionallyInsured:
                  calculateParameters.legalAidAdditionallyInsured,
                winterSportsAdditionallyInsured:
                  calculateParameters.winterSportsAdditionallyInsured,
                adventurousSportsAdditionallyInsured: false,
                sportsEquipmentAdditionallyInsured: false,
                numberOfInsuredMotorVehicles:
                  calculateParameters.alternativeTransportAdditionallyInsured
                    ? 1
                    : 0,
                numberOfInsuredCaravans: 0,
                numberOfInsuredTrailers: 0,
                insuredAmountTravelCancellation:
                  calculateParameters.insuredAmountTravelCancellation,
                partnerAdditionallyInsured:
                  calculateParameters.partnerBirthdates.length > 0,
                additionalInsuredAmountBicycle: 0,
                businessAgentAdditionallyInsured: false,
                compositeTripAdditionallyInsured: false,
                insuredAmountMoneyChecks: 0,
                insuredAmountTravelLuggage: 1000,
                insuredAmountUnderwaterSportsEquipment: 0,
                roadsideAssistanceAdditionallyInsured:
                  calculateParameters.roadsideAssistanceAdditionallyInsured,
              },
            ],
          },
        ],
        commonTechnical: [
          {
            sessionId,
            senderId: credentials['clientId'],
          },
        ],
      },
    };
  }

  public validateSuppliersVariables(
    result: MultiTripTravel_CalculationResult
  ): object {
    const productKrns = result.policyPolicyDetails?.map((item) => {
      return item.productCode;
    });

    return {
      productKrns: productKrns,
      productCategory: 'TRAVEL',
    };
  }

  public validateEmailVariables(
    comparisonId: string | number,
    email: string,
    productType: string
  ): object {
    return {
      comparisonId,
      email,
      productType,
    };
  }

  public orderMultiTripVariables(
    comparisonId: string,
    chosenProductKrn: string,
    sessionId: string,
    senderId: string
  ): object {
    return {
      comparisonId: comparisonId,
      chosenProductKrn: chosenProductKrn,
      request: {
        policyPolicyDetails: {},
        commonTechnical: [
          {
            sessionId: sessionId,
            senderId: senderId,
            googleAnalyticsId: this.getCookie('_ga'),
            googleAnalyticsSessionId: this.getCookie(
              this.getAnalyticsSessionIdCookieName()
            ),
          },
        ],
      },
    };
  }

  public featuresVariables(
    parentProductIds: string[],
    productIds: string[],
    after: string | undefined
  ): object {
    return {
      where: {
        and: [
          {
            or: [
              {
                parentProductId: {
                  in: parentProductIds.map((id) => parseInt(id)),
                },
              },
              {
                productId: {
                  in: productIds.map((id) => parseInt(id)),
                },
              },
            ],
          },
          {
            externalFeatureId: {
              in: [
                25704, 25304, 25278, 25297, 25239, 25237, 25244, 25304, 25278,
                25297, 25342, 25264, 25281, 25269, 25274, 25377, 25399, 25305,
                25239, 25237,
              ],
            },
          },
        ],
      },
      after,
    };
  }

  public calculationInputVariables(comparisonId: string) {
    return {
      comparisonId: comparisonId,
    };
  }

  private getCookie(cookieName) {
    let cookie = {};
    document.cookie.split(';').forEach((el) => {
      let [key, value] = el.split('=');
      cookie[key.trim()] = value;
    });
    return cookie[cookieName];
  }

  private getAnalyticsSessionIdCookieName = () => {
    const cookieNameStart = document.cookie.substring(
      document.cookie.indexOf('_ga_')
    );
    return cookieNameStart.substring(0, cookieNameStart.indexOf('='));
  };

  private getChildBirthdates(
    searchCriteria: object
  ): MultiTripTravel_ChildInput[] {
    let retVal: MultiTripTravel_ChildInput[] = [];

    if (searchCriteria['childBirthdate1']) {
      const childBirthdate = moment(
        searchCriteria['childBirthdate1'],
        'DD-MM-YYYY'
      ).format('YYYY-MM-DD');

      retVal.push({ birthDate: childBirthdate });
    }

    if (searchCriteria['childBirthdate2']) {
      const childBirthdate = moment(
        searchCriteria['childBirthdate2'],
        'DD-MM-YYYY'
      ).format('YYYY-MM-DD');

      retVal.push({ birthDate: childBirthdate });
    }

    if (searchCriteria['childBirthdate3']) {
      const childBirthdate = moment(
        searchCriteria['childBirthdate3'],
        'DD-MM-YYYY'
      ).format('YYYY-MM-DD');

      retVal.push({ birthDate: childBirthdate });
    }

    if (searchCriteria['childBirthdate4']) {
      const childBirthdate = moment(
        searchCriteria['childBirthdate4'],
        'DD-MM-YYYY'
      ).format('YYYY-MM-DD');

      retVal.push({ birthDate: childBirthdate });
    }

    if (searchCriteria['childBirthdate5']) {
      const childBirthdate = moment(
        searchCriteria['childBirthdate5'],
        'DD-MM-YYYY'
      ).format('YYYY-MM-DD');

      retVal.push({ birthDate: childBirthdate });
    }

    return retVal;
  }
}
